// src/hooks/useDebounce.js

import { useState, useEffect } from 'react';

/**
 * A custom React hook that debounces a value.
 * It's used to delay the execution of a function or effect until after
 * a certain amount of time has passed since the last time the value changed.
 * This is perfect for search inputs to avoid sending API requests on every keystroke.
 *
 * @param {*} value The value to be debounced (e.g., a search term from an input).
 * @param {number} delay The debounce delay in milliseconds (e.g., 500).
 * @returns The debounced value, which only updates after the specified delay.
 */
export default function useDebounce(value, delay) {
    // State to store the debounced value
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(
        () => {
            // Set up a timer to update the debounced value after the delay
            const handler = setTimeout(() => {
                setDebouncedValue(value);
            }, delay);

            // Clean up the timer if the value changes before the delay has passed.
            // This is the core of the debounce logic: we reset the timer on each change.
            return () => {
                clearTimeout(handler);
            };
        },
        [value, delay] // Only re-run the effect if the value or delay changes
    );

    return debouncedValue;
}